import React, { useEffect, useState } from "react";
import { Input } from "@component/input";
import { Button } from "@component/button";
import {
    SearchIcon,
    ChevronDown,
    ArrowLeftIcon,
    XIcon,
} from "lucide-react";
import { Job } from "@type/job";
import { Applicant, MatchLevelItem } from "@type/applicant";
import { applicationStatusMap, defaultPagination, matchLevelApplicantOptions, statusSuggestionCandidationOptions } from "@constant/app";
import { EmptyState } from "@component/empty-state";
import { getSuggestedCandidates } from "@service/job";
import { Link } from "react-router";
import LoadingBox from "@component/loading-box";
import CandidateItem from "@component/candidate-item";
import { checkHRViewLimit } from "@service/employer";
import DialogViewLimit from "./dialog/dialog-view-limit";
import DrawerCandidateProfile from "./drawer/drawer-candidate-profile";
import { MetaPagination } from "@type/common";
import Pagination from "@component/pagination";

interface Props {
    job: Job | null;
}
const LIMIT = 20;

export function SuggestionCandidateList({ job }: Props) {
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setLoading] = useState<boolean>(true);
    const [, setError] = useState<string | null>(null);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState("All Statuses");
    const [matchFilter, setMatchFilter] = useState<MatchLevelItem>({
        label: "All Match Levels",
        value: "all"
    });
    const [applicants, setApplicants] = useState<Applicant[]>([]);
    const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(
        null,
    );
    const [isOpenModalViewLimit, setOpenModalViewLimit] = useState<boolean>(false);
    const [isShowCandidateDrawer, setShowCandidateDrawer] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(defaultPagination.page);
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })

    const onSearch = async (keyword: string, application_status: string, matchLevel: string) => {
        setError(null);

        setLoading(true);
        getSuggestedCandidates({
            job_id: job?.id || "",
            page: currentPage,
            limit: LIMIT,
            keyword: keyword,
            application_status: application_status !== "All Statuses" ? application_status : "",
            match_level: matchLevel !== "all" ? matchLevel : "",
        }).then((resp) => {
            if (resp.data.code === 200) {
                setApplicants(resp.data.data || []);
                setMetaPagination(resp.data.meta.pagination);
            }
        }).catch((err) => {
            console.error(`Failed to fetch applicants for job:`, err);
            setError("Failed to load applicants. Please try again later.");
            setApplicants([]);
        }).finally(() => {
            setLoading(false);
        })
    }

    useEffect(() => {
        if (job) {
            onSearch(searchQuery, statusFilter, matchFilter.value);
        }
    }, [job, statusFilter, matchFilter])

    const handleOnChangeSearch = (keyword: string) => {
        if (keyword === "") {
            onSearch("", statusFilter, matchFilter.value);
        }
    }

    const handleViewProfile = (applicant: Applicant) => {
        checkHRViewLimit(applicant.applicant_id).then((resp) => {
            if (!resp.data.data.allowed) {
                setOpenModalViewLimit(true);
            } else {
                setShowCandidateDrawer(true);
                setSelectedApplicant(applicant);
            }
        }).catch((error) => {
            if (error.response.status === 403) {
                setOpenModalViewLimit(true);
            }
        })
    };

    const handleCloseProfile = () => {
        setShowCandidateDrawer(false);
        setSelectedApplicant(null);
    };

    const handleSearchApplication = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSearch(searchQuery, statusFilter, matchFilter.value || "");
    }

    // Toggle dropdown visibility
    const toggleDropdown = (dropdown: string) => {
        setOpenDropdown(openDropdown === dropdown ? null : dropdown);
    };

    // Handle status selection
    const handleStatusSelect = (status: string) => {
        setStatusFilter(status);
        setOpenDropdown(null);
    };

    // Handle match level selection
    const handleMatchLevelSelect = (matchLevel: MatchLevelItem) => {
        setMatchFilter(matchLevel);
        setOpenDropdown(null);
    };

    const onLoadPagination = (page: number) => {
        setCurrentPage(page);

        getSuggestedCandidates({
            job_id: job?.id || "",
            page: currentPage,
            limit: LIMIT,
            keyword: searchQuery,
            application_status: statusFilter !== "All Statuses" ? statusFilter : "",
            match_level: matchFilter.value !== "all" ? matchFilter.value : "",
        }).then((resp) => {
            if (resp.data.code === 200) {
                setApplicants(resp.data.data || []);
                setMetaPagination(resp.data.meta.pagination);
            }
        }).catch((err) => {
            console.error(`Failed to fetch applicants for job:`, err);
            setError("Failed to load applicants. Please try again later.");
            setApplicants([]);
        }).finally(() => {
            setLoading(false);
        })
    }

    const onClearSearch = () => {
        setSearchQuery("");
        onSearch("", statusFilter, matchFilter.value || "");
    }

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg relative">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <div className="flex flex-row">
                    <Link to={`/jobs/${job?.id || ""}/applications`} className="mr-4 mt-1">
                        <ArrowLeftIcon className="h-5 w-5 text-gray-500 hover:text-gray-700 transition-colors" />
                    </Link>
                    <div className="flex flex-col">
                        <h2 className="text-lg font-semibold dark:text-white">
                            Suggested Candidates for {job?.job_title}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Review profiles of candidates that match the skills and experiences required for this job application and invite them to apply.<br />
                            Invitations will expire after one week. You can only send a follow-up invitation once.
                        </p>
                    </div>
                </div>
            </div>
            <div className="p-4 flex sm:flex-row flex-col flex-wrap gap-2">
                <form onSubmit={handleSearchApplication} className="relative flex-1">
                    <SearchIcon
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                    />

                    <Input
                        className="pl-9 w-full"
                        placeholder="Search candidates by location or position..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value)
                            handleOnChangeSearch(e.target.value)
                        }}
                    />
                    <button type="submit" className="hidden">Find</button>

                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={onClearSearch}
                                className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                aria-label="Clear search"
                            >
                                <XIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </form>

                <div className="flex sm:flex-row flex-col gap-2">
                    <div className="relative dropdown-container">
                        <Button
                            variant="outline"
                            className="flex items-center justify-between sm:w-48 w-full text-left pl-3 relative"
                            onClick={() => toggleDropdown("status")}
                        >
                            <span>{statusFilter}{statusFilter !== "All Statuses" ? <span className="ml-1 text-xs px-1.5 py-0.5 border border-gray-300 rounded-sm">{(applicants || []).length}</span> : ""}</span>
                            <ChevronDown size={16} className="ml-2" />
                        </Button>
                        {openDropdown === "status" && (
                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                                <div className="py-1">
                                    {statusSuggestionCandidationOptions.map((status, index) => (
                                        <div
                                            key={index}
                                            className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${statusFilter === status
                                                ? "bg-gray-50 font-medium"
                                                : ""
                                                }`}
                                            onClick={() => handleStatusSelect(status)}
                                        >
                                            {status}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="relative dropdown-container">
                        <Button
                            variant="outline"
                            className="flex items-center justify-between sm:w-40 w-full text-left pl-3"
                            onClick={() => toggleDropdown("match")}
                        >
                            <span>{matchFilter.label}</span>
                            <ChevronDown size={16} className="ml-2" />
                        </Button>
                        {openDropdown === "match" && (
                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                                <div className="py-1">
                                    {matchLevelApplicantOptions.map((level, index) => (
                                        <div
                                            key={index}
                                            className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${matchFilter.value === level.value ? "bg-gray-50 font-medium" : ""
                                                }`}
                                            onClick={() => handleMatchLevelSelect(level)}
                                        >
                                            {level.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Candidate
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Location
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Match
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {isLoading ? (
                            <tr>
                                <td colSpan={5}>
                                    <LoadingBox />
                                </td>
                            </tr>
                        ) : (
                            <>
                                {applicants.length > 0 ? applicants.map((applicant, index) => (
                                    <CandidateItem
                                        key={index}
                                        applicant={applicant}
                                        job={job}
                                        isResend={(applicant.status !== applicationStatusMap.notInvited)}
                                        isDisabledInvitation={(applicant.status === applicationStatusMap.invitedFUSent || applicant.status === applicationStatusMap.invitedToApply)}
                                        onViewProfile={() => handleViewProfile(applicant)}
                                        onLoad={() => onSearch(searchQuery, statusFilter, matchFilter.value)}
                                    />
                                )) : (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                            <EmptyState
                                                icon={(
                                                    <SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                                                )}
                                                title="No candidate found"
                                                description={
                                                    <>
                                                        There are no suggested candidates for this job listing at the moment.<br />Please try again later.
                                                    </>
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
                <div className="p-4">
                    <Pagination
                        isLoading={isLoading}
                        currentPage={currentPage}
                        totalPages={metaPagination.total_pages || 0}
                        onPageChange={onLoadPagination}
                    />
                </div>
            </div>

            {isShowCandidateDrawer && selectedApplicant && (
                <DrawerCandidateProfile
                    isShowHireButton
                    isShowPercentMatch
                    isSuggestionCandidate
                    isOpen={!!selectedApplicant}
                    cvID={selectedApplicant.cv_id}
                    candidate={selectedApplicant.cv_data}
                    applicant={selectedApplicant}
                    job={job}
                    user={{
                        id: selectedApplicant.applicant_id,
                        first_name: selectedApplicant.applicant_name,
                        profile_picture_path: selectedApplicant.profile_picture_path || null,
                        applied_to_job_id: selectedApplicant.applied_to_job_id || [],
                        cv_id: selectedApplicant.cv_id,
                        email: selectedApplicant.main_email,
                        phone_number: selectedApplicant.phone_number,
                        country_code: selectedApplicant.country_code,
                        last_name: "",
                        verification_status: "",
                        role: "",
                        username: "",
                        created_at: "",
                    }}
                    applicant_status={selectedApplicant.status || ""}
                    similarity_score={Number(selectedApplicant.similarity_score) || 0}
                    onClose={handleCloseProfile}
                    onReload={() => {
                        handleCloseProfile();
                        onSearch(searchQuery, statusFilter, matchFilter.value);
                    }}
                />
            )}

            <DialogViewLimit
                open={isOpenModalViewLimit}
                onClose={setOpenModalViewLimit}
                title="Profile Limit Reached"
                description={(
                    <p className="text-gray-500">
                        Our trial gives you limited candidate views so you can experience the power of our AI-driven screening and matching.
                        <br /><br />Contact <u>hello@ourteam.ai</u> to get your full access today.
                    </p>
                )}
            />
        </div>
    );
}
